import { getData } from "./SmileService";
import { SECRET_KEYS } from "../constants/constants";

/**
 * postgrid-address-service.js
 * ---------------------------------------------------------------------------
 * PostGrid address autocomplete + verification with Canada Post (SERP-style)
 * line1 formatting: "UNIT-CIVIC STREETNAME STREETTYPE DIR"
 * e.g. "2-106 RAILWAY AVE" instead of "106 RAILWAY AVE #2"
 *
 * Flow (matches PostGrid's recommended integration — 1 billable lookup
 * per selection + 1 verification):
 *
 *   1. findByKeyword(keyword)            → GET  /addver/completions   (free previews)
 *   2. user selects a suggestion
 *   3. retrieveAddress(keyword, index)   → POST /addver/completions?index=N (1 lookup)
 *                                          → then verifyAddress(...) internally
 *   4. verifyAddress(line1, city, pc)    → POST /addver/verifications?includeDetails=true
 *                                          → line1 rebuilt in Canada Post format
 *
 * External dependencies (already defined elsewhere in your app):
 *   - getData(baseUrl, path)   → your existing secret-fetching helper
 *   - postGridApiUrl           → e.g. "https://api.postgrid.com/v1"
 *   - SECRET_KEYS              → constants object with the Key Vault secret name
 * ---------------------------------------------------------------------------
 */

// ---------------------------------------------------------------------------
// Configuration / constants
// ---------------------------------------------------------------------------

// If these are defined in another module in your project, import them instead
// and delete these placeholders.
const postGridApiUrl = process.env.REACT_APP_POSTGRID_API_URL;


/**
 * Set to true if you want "2-106 Railway Ave" (mixed case) instead of
 * "2-106 RAILWAY AVE". Canada Post machine-readable standard is uppercase,
 * so keep false for data you persist to CE / print through Canada Post.
 */
const USE_PROPER_CASE = false;

// ---------------------------------------------------------------------------
// API key caching
// ---------------------------------------------------------------------------
// Avoids a Key Vault round trip on every keystroke. The key is fetched once
// per page session and reused. If a request ever fails with 401, call
// invalidatePostGridApiKey() and retry once.
// ---------------------------------------------------------------------------

let _apiKeyPromise = null;

function getPostGridApiKey() {
  if (!_apiKeyPromise) {
    _apiKeyPromise = getData(
      "",
      `/api/get-secret/${SECRET_KEYS.WVC_POSTGRID_API_KEY_SECRET_NAME}`
    ).then((secret) => {
      if (!secret?.value) {
        throw new Error("PostGrid API key secret returned empty value");
      }
      return secret.value;
    }).catch((err) => {
      // Reset so the next call retries instead of caching the failure forever
      _apiKeyPromise = null;
      throw err;
    });
  }
  return _apiKeyPromise;
}

function invalidatePostGridApiKey() {
  _apiKeyPromise = null;
}

// ---------------------------------------------------------------------------
// Canada Post formatting helpers
// ---------------------------------------------------------------------------

/**
 * Rebuild line1 in Canada Post civic format from the verification response's
 * parsed `details` object:
 *
 *   suiteID=2, streetNumber=106, streetName=RAILWAY, streetType=AVE
 *     → "2-106 RAILWAY AVE"
 *
 * Falls back to PostGrid's own line1 when details are missing or the address
 * is non-civic (PO Box, rural route, general delivery, failed verification).
 * For those, PostGrid's line1 is already correct — do not reformat it.
 *
 * @param {object} data - the `data` object from POST /addver/verifications
 * @returns {string}
 */
function buildCanadaPostLine1(data) {
  const d = data?.details;

  if (!d || !d.streetNumber || !d.streetName) {
    return data?.line1 || "";
  }

  const civicPart = [d.streetNumber, d.streetName, d.streetType, d.streetDirection]
    .filter(Boolean)
    .join(" ");

  return d.suiteID ? `${d.suiteID}-${civicPart}` : civicPart;
}

/**
 * DISPLAY-ONLY transform for autocomplete previews so the dropdown shows the
 * Canada Post style before the user selects:
 *
 *   "106 RAILWAY AVE #2"      → "2-106 RAILWAY AVE"
 *   "106 RAILWAY AVE APT 2"   → "2-106 RAILWAY AVE"
 *   "106 RAILWAY AVE UNIT 2"  → "2-106 RAILWAY AVE"
 *
 * This is string surgery on a preview, NOT a source of truth. Always run the
 * selected address through verifyAddress() before persisting.
 *
 * @param {string} text
 * @returns {string}
 */
function toCanadaPostDisplay(text) {
  if (!text) return "";

  const normalized = normalizeUnitHyphen(text);

  // Trailing "#2" style
  let m = normalized.match(/^(.*?)\s*#\s*([A-Za-z0-9]+)$/);
  if (m) return `${m[2]}-${m[1].trim()}`;

  // Trailing "APT 2" / "UNIT 2" / "SUITE 2" / "STE 2" style
  m = normalized.match(/^(.*?)\s+(?:APT|UNIT|SUITE|STE)\s+([A-Za-z0-9]+)$/i);
  if (m) return `${m[2]}-${m[1].trim()}`;

  return normalized; // already unit-first ("2-106 ...") or no unit at all
}

function normalizeUnitHyphen(text) {
  if (!text) return "";
  // Collapse "2 - 106", "2-106", "2  -106", etc. into "2-106"
  // Only touches a leading "<unit> - <number>" pattern, leaves the rest alone.
  return text.replace(/^(\d+[A-Za-z]?)\s*-\s*(\d)/, "$1-$2");
}

// ---------------------------------------------------------------------------
// 1. Autocomplete previews (free — no billable lookup)
// ---------------------------------------------------------------------------

/**
 * Fetch autocomplete suggestions for a partial street keyword.
 *
 * NOTE on previews: GET /completions returns partial data by design.
 * `preview.pc` is only the first 3 characters (FSA) of the postal code.
 * The full postal code arrives from retrieveAddress()/verifyAddress().
 *
 * @param {string} keyword - what the user has typed so far
 * @returns {Promise<Array<object>>} suggestion objects for the dropdown
 */
async function findByKeyword(keyword) {
  if (!keyword?.trim()) {
    return [];
  }

  try {
    const apiKey = await getPostGridApiKey();

    // Normalize spacing around a leading unit hyphen ("2 - 106" -> "2-106")
    // BEFORE sending to PostGrid — not just when displaying results.
    const normalizedKeyword = normalizeUnitHyphen(keyword.trim());

    // URLSearchParams handles encoding of '#', '&', spaces, accents, etc.
    // (The previous multi-line template literal embedded a newline +
    //  indentation into the URL, breaking countryFilter.)
    const params = new URLSearchParams({
      partialStreet: normalizedKeyword,
      countryFilter: "CA"
    });

    const response = await fetch(
      `${postGridApiUrl}/addver/completions?${params}`,
      {
        method: "GET",
        headers: {
          "x-api-key": apiKey,
          "Content-Type": "application/json"
        }
      }
    );

    if (!response.ok) {
      if (response.status === 401) invalidatePostGridApiKey();
      const errorText = await response.text();
      console.error("PostGrid Completions Error:", errorText);
      throw new Error(`PostGrid API Error: ${response.status}`);
    }

    const result = await response.json();

    return (result.data || []).map((item, index) => {
      const rawText = item.preview?.address || item.address || "";
      const fsaOnly =
        item.preview?.postalOrZip || item.preview?.pc || item.postalOrZip || "";

      return {
        Id: item.id ?? index,
        // Index into the SAME keyword when calling POST /completions?index=N
        Index: index,

        // Canada Post style shown in the dropdown (display-only transform)
        Text: toCanadaPostDisplay(rawText),

        City: item.preview?.city || "",
        ProvinceCode:
          item.preview?.provinceCode || item.preview?.province || "",

        // FSA (first 3 chars) only at preview stage — full code comes later
        PostalCode: fsaOnly,

        Description: [
          item.preview?.city,
          item.preview?.provinceCode || item.preview?.province,
          fsaOnly
        ]
          .filter(Boolean)
          .join(", "),

        Next: "Retrieve",
        RawData: item // untouched original, kept as source of truth
      };
    });
  } catch (error) {
    console.error("PostGrid lookup failed:", error);
    return [];
  }
}

// ---------------------------------------------------------------------------
// 2. Retrieve full address for the selected suggestion (1 billable lookup)
// ---------------------------------------------------------------------------

/**
 * Resolve the full address for the suggestion the user selected, then
 * standardize it to Canada Post format via verification.
 *
 * IMPORTANT: `keyword` must be the SAME partialStreet string used in the
 * findByKeyword() call that produced the suggestion list, and `selectedIndex`
 * is the position of the chosen item in that list (the `Index` property on
 * the suggestion object).
 *
 * @param {string} keyword       - the original search keyword
 * @param {number} selectedIndex - index of the chosen suggestion
 * @returns {Promise<object>}    - standardized address (see verifyAddress)
 */
async function retrieveAddress(keyword, selectedIndex) {
  const apiKey = await getPostGridApiKey();
  const normalizedKeyword = normalizeUnitHyphen(keyword.trim());
  const response = await fetch(
    `${postGridApiUrl}/addver/completions?index=${encodeURIComponent(selectedIndex)}`,
    {
      method: "POST",
      headers: {
        "x-api-key": apiKey,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        partialStreet: normalizedKeyword,
        countryFilter: "CA"
      })
    }
  );

  if (!response.ok) {
    if (response.status === 401) invalidatePostGridApiKey();
    const errorText = await response.text();
    console.error("PostGrid Retrieve Error:", errorText);
    throw new Error(`PostGrid API Error: ${response.status}`);
  }

  const result = await response.json();

  // With ?index=N supplied, data is a single object (not an array).
  // Full completion schema: data.address = { address, city, prov, pc, country }
  const completed = result.data?.address || {};

  // Standardize to Canada Post format (and get the full postal code,
  // province, verification status, etc.)
  return verifyAddress(
    completed.address || "",
    completed.city || "",
    completed.pc || ""
  );
}

// ---------------------------------------------------------------------------
// 3. Verification + Canada Post standardization
// ---------------------------------------------------------------------------

/**
 * Verify an address against the Canada Post database and return it with
 * line1 rebuilt in Canada Post civic format ("2-106 RAILWAY AVE").
 *
 * Input line1 can be in ANY style — "106 Railway Ave #2", "2-106 Railway Ave",
 * "106 Railway Ave Apt 2" — PostGrid's freeform parser resolves them all.
 *
 * @param {string} line1        - street line in any format
 * @param {string} [city]       - city (optional but improves match quality)
 * @param {string} [postalCode] - postal code if known (optional)
 * @returns {Promise<{
 *   line1: string,
 *   city: string,
 *   provinceCode: string,
 *   postalCode: string,
 *   country: string,
 *   status: 'verified'|'corrected'|'failed'|string,
 *   errors: object,
 *   details: object|null,
 *   raw: object
 * }>}
 */
async function verifyAddress(line1, city, postalCode) {
  const apiKey = await getPostGridApiKey();

  const params = new URLSearchParams({ includeDetails: "true" });
  if (USE_PROPER_CASE) params.set("properCase", "true");

  const addressBody = {
    line1: line1,
    country: "CA"
  };
  if (city) addressBody.city = city;
  if (postalCode) addressBody.postalOrZip = postalCode;

  const response = await fetch(
    `${postGridApiUrl}/addver/verifications?${params}`,
    {
      method: "POST",
      headers: {
        "x-api-key": apiKey,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ address: addressBody })
    }
  );

  if (!response.ok) {
    if (response.status === 401) invalidatePostGridApiKey();
    const errorText = await response.text();
    console.error("PostGrid Verify Error:", errorText);
    throw new Error(`PostGrid API Error: ${response.status}`);
  }

  const result = await response.json();
  const data = result.data || {};

  return {
    // The whole point: Canada Post formatted line1
    line1: buildCanadaPostLine1(data),

    city: data.city || "",
    provinceCode: data.provinceOrState || "",
    postalCode: data.postalOrZip || "",
    country: (data.country || "ca").toUpperCase(),

    // 'verified'  = deliverable as-is
    // 'corrected' = PostGrid fixed something (see errors for what)
    // 'failed'    = could not resolve — surface to the user, do not persist
    status: data.status || "failed",
    errors: data.errors || {},

    // Parsed components — persist these alongside line1 if your schema
    // allows, so downstream systems (Stripe billing, CE) can reformat
    // without re-verifying. Null for PO boxes / rural routes.
    details: data.details || null,

    raw: data
  };
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------
// ES modules:
export const postgridService = {
  findByKeyword,
  retrieveAddress,
  verifyAddress,
  buildCanadaPostLine1,
  toCanadaPostDisplay,
  invalidatePostGridApiKey
};

// If your project uses CommonJS or plain <script> tags instead, replace the
// export block above with:
//
// module.exports = { findByKeyword, retrieveAddress, verifyAddress,
//                    buildCanadaPostLine1, toCanadaPostDisplay,
//                    invalidatePostGridApiKey };